import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/data/categories';

interface CategoryDropdownProps {
  label: string;
  placeholder: string;
  variant?: 'solid' | 'light';
}

export default function CategoryDropdown({
  label,
  placeholder,
  variant = 'solid',
}: CategoryDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const solidStyles =
    'bg-background-50 text-foreground-900 border-background-200 hover:border-background-300';
  const lightStyles =
    'bg-background-50/10 text-background-50 border-background-50/30 hover:bg-background-50/20';

  return (
    <div ref={ref} className="relative w-full">
      <span className="block text-xs font-semibold tracking-wide uppercase mb-2 opacity-80">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
          variant === 'solid' ? solidStyles : lightStyles
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{placeholder}</span>
        <span className="w-5 h-5 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full mt-2 z-30 max-h-80 overflow-y-auto rounded-lg border border-background-200 bg-background-50 shadow-sm p-1"
        >
          {categories.map((c) => (
            <a
              key={c.key}
              href={c.url}
              target="_blank"
              rel="noopener nofollow"
              role="option"
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-800 hover:bg-primary-100/70 hover:text-primary-900 transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-primary-600">
                <i className={`${c.icon} text-base`} />
              </span>
              <span className="truncate">{t(c.key)}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}